import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { t } from 'i18next'
import DynamicTable from 'Components/DynamicTable/DynamicTable'
import CustomSnackbar from 'Components/CustomSnackbar'
import MiniLoading from 'Components/MiniLoading'
import useColumnPickingFlow from './hooks/useColumnPickingFlow'
import AddBranchModal from './components/AddBranchModal/AddBranchModal'
import DeleteBranchModal from './components/DeleteBranchModal/DeleteBranchModal'
import {
  fetchPickingFlowBranches,
  savePickingFlowBranches,
} from 'redux/actions/pickingFlowActions'
import searchIcon from 'Assets/Images/BuscarCotizacion/search.svg'
import './pickingFlowShared.css'
import './PickingFlowManager.css'

const getActiveIdSet = branches =>
  new Set(
    branches
      .filter(branch => branch.Active)
      .map(branch => branch.BranchOfficeId)
  )

const PickingFlowManager = () => {
  const dispatch = useDispatch()

  const [catalog, setCatalog] = useState([])
  const [workingBranches, setWorkingBranches] = useState([])
  const [searchText, setSearchText] = useState('')
  const [loadingData, setLoadingData] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState(null)
  const [snackbarMessage, setSnackbarMessage] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const loadBranches = async () => {
    setLoadingData(true)
    try {
      const branches = await dispatch(fetchPickingFlowBranches())
      const activeIds = getActiveIdSet(branches)

      setCatalog(branches)
      setWorkingBranches(
        branches.filter(branch => activeIds.has(branch.BranchOfficeId))
      )
    } catch (error) {
      console.error('Error loading picking flow branches:', error)
      setSnackbarMessage({
        open: true,
        message: t('PICKING_FLOW_SETTINGS.MESSAGE.ERROR_LOAD'),
        severity: 'error',
      })
    } finally {
      setLoadingData(false)
    }
  }

  useEffect(() => {
    loadBranches().then()
  }, [])

  const savedActiveIds = useMemo(() => getActiveIdSet(catalog), [catalog])

  const hasPendingChanges = useMemo(
    () =>
      workingBranches.length !== savedActiveIds.size ||
      workingBranches.some(
        branch => !savedActiveIds.has(branch.BranchOfficeId)
      ),
    [workingBranches, savedActiveIds]
  )

  const filteredWorkingBranches = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase()
    if (!normalizedSearch) return workingBranches

    return workingBranches.filter(branch =>
      (branch.Name || '').toLowerCase().includes(normalizedSearch)
    )
  }, [workingBranches, searchText])

  const availableBranches = useMemo(() => {
    const workingIds = new Set(
      workingBranches.map(branch => branch.BranchOfficeId)
    )
    return catalog.filter(branch => !workingIds.has(branch.BranchOfficeId))
  }, [catalog, workingBranches])

  const handleCloseSnackbar = () => {
    setSnackbarMessage(prev => ({ ...prev, open: false }))
  }

  const handleOpenDeleteModal = useCallback(branch => {
    setDeleteTarget(branch)
  }, [])

  const handleConfirmDelete = () => {
    setWorkingBranches(prev =>
      prev.filter(
        branch => branch.BranchOfficeId !== deleteTarget.BranchOfficeId
      )
    )
    setDeleteTarget(null)
  }

  const handleAddBranches = selectedBranches => {
    setWorkingBranches(prev => [...prev, ...selectedBranches])
    setIsAddModalOpen(false)
  }

  const handleSave = async () => {
    setIsSaving(true)
    const activeIds = workingBranches.map(branch => branch.BranchOfficeId)
    const previouslyActiveBranches = catalog.filter(branch => branch.Active)
    const success = await dispatch(
      savePickingFlowBranches(workingBranches, previouslyActiveBranches)
    )
    setIsSaving(false)

    if (success) {
      setCatalog(prev =>
        prev.map(branch => ({
          ...branch,
          Active: activeIds.includes(branch.BranchOfficeId),
        }))
      )
      setSnackbarMessage({
        open: true,
        message: t('PICKING_FLOW_SETTINGS.MESSAGE.SUCCESS_SAVE'),
        severity: 'success',
      })
    } else {
      await loadBranches()
      setSnackbarMessage({
        open: true,
        message: t('PICKING_FLOW_SETTINGS.MESSAGE.ERROR_SAVE'),
        severity: 'error',
      })
    }
  }

  const handleCancel = () => {
    setWorkingBranches(
      catalog.filter(branch => savedActiveIds.has(branch.BranchOfficeId))
    )
  }

  const columns = useColumnPickingFlow({ onDelete: handleOpenDeleteModal })

  return (
    <>
      <div
        className="picking-flow-manager"
        data-testid="container-picking-flow-manager">
        <div className="picking-flow-toolbar">
          <div className="picking-flow-search-group">
            <input
              type="text"
              data-testid="input-picking-flow-search"
              placeholder={t('PICKING_FLOW_SETTINGS.LIST.SEARCH_PLACEHOLDER')}
              value={searchText}
              onChange={event => setSearchText(event.target.value)}
            />
            <img
              src={searchIcon}
              className="picking-flow-search-icon"
              alt="search"
            />
          </div>
          <button
            type="button"
            className="picking-flow-btn-solid"
            data-testid="btn-open-add-picking-flow-branch"
            onClick={() => setIsAddModalOpen(true)}>
            {t('PICKING_FLOW_SETTINGS.LIST.ADD_BRANCH')}
          </button>
        </div>

        {loadingData ? (
          <MiniLoading />
        ) : (
          <DynamicTable
            columns={columns}
            data={filteredWorkingBranches}
            loadingData={loadingData}
            getRowId={row => row.BranchOfficeId}
          />
        )}

        <div className="picking-flow-footer">
          <button
            type="button"
            className="picking-flow-btn-outline"
            data-testid="btn-cancel-picking-flow"
            disabled={isSaving || !hasPendingChanges}
            onClick={handleCancel}>
            {t('PICKING_FLOW_SETTINGS.LIST.CANCEL')}
          </button>
          <button
            type="button"
            className="picking-flow-btn-solid"
            data-testid="btn-save-picking-flow"
            disabled={isSaving}
            onClick={handleSave}>
            {t('PICKING_FLOW_SETTINGS.LIST.SAVE')}
          </button>
        </div>
      </div>

      <AddBranchModal
        open={isAddModalOpen}
        availableBranches={availableBranches}
        onAdd={handleAddBranches}
        onClose={() => setIsAddModalOpen(false)}
      />

      <DeleteBranchModal
        open={!!deleteTarget}
        onConfirm={handleConfirmDelete}
        onClose={() => setDeleteTarget(null)}
      />

      <CustomSnackbar
        open={snackbarMessage.open}
        message={snackbarMessage.message}
        severity={snackbarMessage.severity}
        onClose={handleCloseSnackbar}
      />
    </>
  )
}

export default PickingFlowManager
